import React, { useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { ComponentInstance } from "@uniformdev/canvas";
import {
    ComponentProps,
    UniformSlot,
    UniformSlotWrapperComponentProps,
    useUniformCurrentComposition
} from "@uniformdev/canvas-react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useExperiment } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { isProduction } from "@/utils/urls";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics";

import {
    PersonalizationOverrideControls,
    PersonalizationUserSegmentValues
} from "./personalization-controls";
import { UserSegmentTypes, useUserSegments } from "./use-user-segments";

export interface UniformPersonalizationContainerProps extends ComponentProps {
    containerName: string;
    experimentId?: string;
}

interface PersonalizedSectionProps
    extends UniformPersonalizationContainerProps {
    overrides?: PersonalizationUserSegmentValues;
    experimentId?: string;
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>;
}
interface ExperimentallyPersonalizedSectionProps
    extends UniformPersonalizationContainerProps {
    overrides?: PersonalizationUserSegmentValues;
    experimentId: string;
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>;
    variantComponent?: React.ReactNode;
    setVariantStatus: React.Dispatch<
        React.SetStateAction<VariantsStatus | null>
    >;
}

enum VariantsStatus {
    NO_VARIANT_MATCH = "no_variant_match",
    VARIANTS_AVAILABLE = "variants_available",
    FETCHING_SEGMENTS = "fetching_segments",
    SEGMENT_FETCH_ERROR = "segment_fetch_error",
    MISSING_USER_SEGMENTS = "missing_user_segments",
    PERSONALIZATION_DISABLED = "personalization_disabled",
    NOT_ENROLLED_IN_EXPERIMENT = "not_enrolled_in_experiment"
}

const VariantSelector = (props: UniformSlotWrapperComponentProps) => {
    return (
        // this should only be one item, but the SlotWrapper expects an array
        <React.Fragment>
            {props.items.map((item, index) => (
                <React.Fragment key={index}>{item}</React.Fragment>
            ))}
        </React.Fragment>
    );
};

// Helper function to render default slot
const renderDefaultSlot = (
    variantStatus: VariantsStatus,
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>
) => {
    const messages: Record<VariantsStatus, string> = {
        [VariantsStatus.NO_VARIANT_MATCH]:
            "No variants match. Rendering Default",
        [VariantsStatus.MISSING_USER_SEGMENTS]:
            "No User Segments acquired. Rendering Default",
        [VariantsStatus.PERSONALIZATION_DISABLED]:
            "Personalization is disabled. Rendering Default",
        [VariantsStatus.SEGMENT_FETCH_ERROR]:
            "Error fetching User Segments. Rendering Default",
        [VariantsStatus.NOT_ENROLLED_IN_EXPERIMENT]:
            "User not enrolled in experiment. Rendering Default",
        [VariantsStatus.FETCHING_SEGMENTS]:
            "Fetching User Segments. Temporarily rendering Default",
        // never invoked, but needed to satisfy the type system s
        [VariantsStatus.VARIANTS_AVAILABLE]: ""
    };

    setRenderMessage(messages[variantStatus]);
    return <UniformSlot name="default" wrapperComponent={VariantSelector} />;
};

// Helper function to find matching variant index
const findMatchingVariant = (
    variantCandidates: ComponentInstance[],
    personalizationSegmentValues: PersonalizationUserSegmentValues
): number => {
    if (!personalizationSegmentValues) return -1;

    return variantCandidates.findIndex((variantCandidate) => {
        // all user segments specified by the Author in the variant (product, job function, domain type, etc.)
        const segments = variantCandidate.parameters?.["personalizationSegment"]
            ?.value as {
            segmentType: UserSegmentTypes;
            value: string[];
        }[];

        // check if all segments match the user's profile
        return segments.every(({ segmentType, value: segmentTargets }) => {
            const userValueForThisSegment =
                personalizationSegmentValues[segmentType];

            // if the user doesn't have a value for this segment, it can't match
            if (!userValueForThisSegment) {
                return false;
            }

            // handle owned products differently, since it's an array of products.
            if (segmentType === UserSegmentTypes.OWNED_PRODUCTS) {
                // we want to check if any of the user's owned products match one of the author-defined segment's value
                return segmentTargets.some((product) =>
                    (userValueForThisSegment as string[]).includes(product)
                );
            }

            // for all other single-valued segments, simply check if the user's value
            return segmentTargets.includes(
                (userValueForThisSegment as string) || ""
            );
        });
    });
};

// Helper function to get final personalization values
const getFinalPersonalizationValues = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userSegments: Record<string, any> | undefined,
    overrides?: PersonalizationUserSegmentValues
): PersonalizationUserSegmentValues => {
    const finalValues = {
        ...userSegments,
        ...(overrides?.useOverrides ? overrides : {}),
        useOverrides: overrides?.useOverrides || false,
        enrollInExperiment: overrides?.enrollInExperiment || false
    };
    return finalValues;
};

const ExperimentallyPersonalizedSection = ({
    experimentId,
    setVariantStatus,
    overrides,
    setRenderMessage,
    variantComponent
}: ExperimentallyPersonalizedSectionProps) => {
    const experimentStatus = useExperiment({
        experimentName: experimentId,
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Personalization Experiment - configured in Uniform",
            expectedReleaseDate: "unknown - configured in Uniform",
            expectedRuntime: "unknown - configured in Uniform",
            context: {
                urls: []
            }
        }
    });

    const showTreatment =
        experimentStatus.type === "enrolled" && experimentStatus.value;

    useEffect(() => {
        if (!showTreatment && !overrides?.enrollInExperiment) {
            if (setVariantStatus) {
                setVariantStatus(VariantsStatus.NOT_ENROLLED_IN_EXPERIMENT);
            }
        }
    }, [showTreatment, overrides?.enrollInExperiment, setVariantStatus]);

    if (!showTreatment && !overrides?.enrollInExperiment) {
        return renderDefaultSlot(
            VariantsStatus.NOT_ENROLLED_IN_EXPERIMENT,
            setRenderMessage
        );
    }

    return variantComponent;
};

// Helper function to validate personalization prerequisites
const validatePersonalizationPrerequisites = (
    shouldPersonalize: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userSegments: any,
    userSegmentStatus: "idle" | "fetching" | "complete",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any,
    overrides?: PersonalizationUserSegmentValues
): { isValid: boolean; status?: VariantsStatus } => {
    if (userSegmentStatus !== "complete") {
        return {
            isValid: false,
            status: VariantsStatus.FETCHING_SEGMENTS
        };
    }
    if (!shouldPersonalize) {
        return {
            isValid: false,
            status: VariantsStatus.PERSONALIZATION_DISABLED
        };
    }

    if (!userSegments) {
        return {
            isValid: false,
            status: VariantsStatus.MISSING_USER_SEGMENTS
        };
    }

    if (error && !overrides?.useOverrides) {
        return {
            isValid: false,
            status: VariantsStatus.SEGMENT_FETCH_ERROR
        };
    }

    return { isValid: true };
};

// Helper function to create variant component
const createVariantComponent = (chosenVariantIndex: number) => (
    <UniformSlot
        name="variants"
        wrapperComponent={(slotProps) =>
            VariantSelector({
                ...slotProps,
                items: [slotProps.items[chosenVariantIndex]]
            })
        }
    />
);

// Helper function to set render message for variant
const setVariantRenderMessage = (
    variantCandidates: ComponentInstance[],
    chosenVariantIndex: number,
    overrides: PersonalizationUserSegmentValues | undefined,
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>
) => {
    const variantName = getCurrentVariantName(
        variantCandidates,
        chosenVariantIndex
    );
    const sourceType = overrides?.useOverrides ? "Overrides" : "User Segments";

    setRenderMessage(
        `Rendering "${variantName}" Variant based on ${sourceType}`
    );
};

const getCurrentVariantName = (
    variantCandidates: ComponentInstance[],
    chosenVariantIndex: number
) => {
    return (
        variantCandidates[chosenVariantIndex]?.parameters?.["variantName"]
            ?.value || "unknown"
    );
};

const PersonalizedSection = (props: PersonalizedSectionProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();
    const [selectedVariantKey, setSelectedVariantKey] = useState<string | null>(
        null
    );
    const [variantStatus, setVariantStatus] = useState<VariantsStatus | null>(
        null
    );
    const [renderedExperience, setRenderedExperience] =
        useState<React.ReactNode>(null);
    const {
        data: userSegments,
        error,
        status: userSegmentStatus
    } = useUserSegments();
    const shouldPersonalize = checkFeatureFlagGate(
        featureFlags.uniformPersonalizationEnabled
    );

    const composition = useUniformCurrentComposition();
    const compositionId = composition?.data?._id;

    // Memoize slots to prevent infinite loops
    const variantSlots = useMemo(
        () => props.component.slots?.["variants"] ?? [],
        [props.component.slots]
    );

    // Send metrics once when variant changes
    useEffect(() => {
        const validation = validatePersonalizationPrerequisites(
            shouldPersonalize,
            userSegments,
            userSegmentStatus,
            error,
            props.overrides
        );

        if (!validation.isValid) {
            setSelectedVariantKey("default_experience");
            setVariantStatus(validation.status as VariantsStatus);
            setRenderedExperience(
                renderDefaultSlot(
                    validation.status as VariantsStatus,
                    props.setRenderMessage
                )
            );
            return;
        }

        // Process personalization logic
        const personalizationValues = getFinalPersonalizationValues(
            userSegments,
            props.overrides
        );
        const chosenVariantIndex = findMatchingVariant(
            variantSlots,
            personalizationValues
        );

        // Check if variant was found
        if (chosenVariantIndex < 0) {
            setSelectedVariantKey("default_experience");
            setVariantStatus(VariantsStatus.NO_VARIANT_MATCH);
            setRenderedExperience(
                renderDefaultSlot(
                    VariantsStatus.NO_VARIANT_MATCH,
                    props.setRenderMessage
                )
            );
            return;
        }

        const variantName = getCurrentVariantName(
            variantSlots,
            chosenVariantIndex
        ) as string;
        setSelectedVariantKey(variantName);
        setVariantStatus(VariantsStatus.VARIANTS_AVAILABLE);
        setVariantRenderMessage(
            variantSlots,
            chosenVariantIndex,
            props.overrides,
            props.setRenderMessage
        );

        const variantComponent = createVariantComponent(chosenVariantIndex);
        setRenderedExperience(variantComponent);

        // Handle experimental personalization
        if (props.experimentId) {
            setRenderedExperience(
                <ExperimentallyPersonalizedSection
                    containerName={props.containerName}
                    setRenderMessage={props.setRenderMessage}
                    setVariantStatus={setVariantStatus}
                    experimentId={props.experimentId}
                    variantComponent={variantComponent}
                    overrides={props.overrides}
                    component={props.component}
                />
            );
        }
        return;
    }, [
        error,
        props.overrides,
        props.setRenderMessage,
        props.experimentId,
        props.containerName,
        props.component,
        shouldPersonalize,
        userSegmentStatus,
        userSegments,
        variantSlots,
        setVariantStatus
    ]);

    useEffect(() => {
        if (!selectedVariantKey || !variantStatus) {
            return;
        }
        if (analyticsClient) {
            createAnalyticsEvent({
                name: "waePersonalizationVariantRender",
                action: "personalizationVariantRender",
                actionSubject: "uniformPage",
                eventType: "screen",
                source: window.location.pathname,
                attributes: {
                    containerName: props.containerName,
                    path: window.location.pathname,
                    compositionId: compositionId,
                    variantName: selectedVariantKey,
                    status: variantStatus
                }
            }).fire();
        }
    }, [
        compositionId,
        createAnalyticsEvent,
        selectedVariantKey,
        props.containerName,
        variantStatus,
        analyticsClient
    ]);
    return renderedExperience;
};

const UniformPersonalizationContainer = (
    props: UniformPersonalizationContainerProps
) => {
    const isProd = isProduction();
    const [overrides, setOverrides] =
        useState<PersonalizationUserSegmentValues>({
            useOverrides: false,
            enrollInExperiment: false
        });
    const [renderMessage, setRenderMessage] = useState<string>("");
    const [isContainerHovered, setIsContainerHovered] =
        useState<boolean>(false);

    return (
        <div
            css={css({
                "position": "relative",
                // Blue border, dog-ear controls, and hover effects only show in pre-production environments
                "border":
                    !isProd && isContainerHovered
                        ? `2px solid ${wacToken("color.extended.blue300")}`
                        : "2px solid transparent",
                "borderRadius": !isProd ? wacToken("corner.radius.card") : "0",
                "padding": token("space.100"),
                "transition": !isProd ? "border-color 0.2s ease" : "none",
                ":hover": !isProd
                    ? {
                          border: `2px solid ${wacToken(
                              "color.extended.blue300"
                          )}`
                      }
                    : {}
            })}
            onMouseEnter={
                !isProd ? () => setIsContainerHovered(true) : undefined
            }
            onMouseLeave={
                !isProd ? () => setIsContainerHovered(false) : undefined
            }
        >
            {/* Personalization controls only render in pre-production environments */}
            {!isProd && (
                <PersonalizationOverrideControls
                    values={overrides}
                    setValues={setOverrides}
                    renderMessage={renderMessage}
                    isContainerHovered={isContainerHovered}
                />
            )}
            <PersonalizedSection
                {...props}
                overrides={overrides}
                setRenderMessage={setRenderMessage}
                experimentId={props.experimentId}
            />
        </div>
    );
};

export default UniformPersonalizationContainer;
